import { Component, OnDestroy } from '@angular/core';
import { EMPTY, interval, Subscription } from 'rxjs';
import { catchError, switchMap, takeWhile } from 'rxjs/operators';
import { DiagnosticService } from '../../services/diagnostic.service';
import { DiagnosticInput, DiagnosticResult } from '../../models/diagnostic.models';
import { StoreAuxService } from '../../../services/storeAux/store-aux.service';

@Component({
  selector: 'app-campaign-troubleshooting',
  templateUrl: './campaign-troubleshooting.component.html',
  styleUrls: ['./campaign-troubleshooting.component.scss']
})
export class CampaignTroubleshootingComponent implements OnDestroy {
  loading = false;
  error: string | null = null;
  result: DiagnosticResult | null = null;

  private readonly POLL_INTERVAL_MS = 4000;
  private readonly POLL_TIMEOUT_MS = 180_000;

  private lastInput: DiagnosticInput | null = null;
  private diagSub: Subscription | null = null;

  constructor(
    private readonly diagnosticService: DiagnosticService,
    readonly storeAux: StoreAuxService
  ) {}

  onSubmit(input: DiagnosticInput): void {
    this.lastInput = input;
    this.runDiagnosis(input);
  }

  onRetry(): void {
    if (this.lastInput) {
      this.runDiagnosis(this.lastInput);
    }
  }

  private runDiagnosis(input: DiagnosticInput): void {
    this.loading = true;
    this.error = null;
    this.result = null;
    this.diagSub?.unsubscribe();

    const started = Date.now();

    this.diagSub = this.diagnosticService.enqueue(input).pipe(
      switchMap(({ job_id }) =>
        interval(this.POLL_INTERVAL_MS).pipe(
          switchMap(() => this.diagnosticService.getJob(job_id)),
          takeWhile(job => {
            const isInProgress = job.status === 'pending' || job.status === 'processing';
            if (!isInProgress) {
              return false;
            }
            if (Date.now() - started > this.POLL_TIMEOUT_MS) {
              this.error = 'El diagnóstico tardó demasiado. Intenta de nuevo.';
              this.loading = false;
              return false;
            }
            return true;
          }, true),
          catchError((err: any) => {
            const detail = err?.error?.detail;
            const msg = Array.isArray(detail)
              ? detail.map((d: any) => d.msg || JSON.stringify(d)).join(' · ')
              : (typeof detail === 'string' ? detail : null);
            const serverMessage = typeof err?.error?.message === 'string' ? err.error.message : null;
            this.error = err?.status === 404
              ? 'El resultado expiró. Intenta de nuevo.'
              : (msg || serverMessage || err?.message || 'Error inesperado');
            this.loading = false;
            return EMPTY;
          })
        )
      ),
      catchError((err: any) => {
        const detail = err?.error?.detail;
        const msg = Array.isArray(detail)
          ? detail.map((d: any) => d.msg || JSON.stringify(d)).join(' · ')
          : (typeof detail === 'string' ? detail : null);
        const serverMessage = typeof err?.error?.message === 'string' ? err.error.message : null;
        this.error = msg || serverMessage || err?.message || 'Error inesperado';
        this.loading = false;
        return EMPTY;
      })
    ).subscribe(job => {
      if (job.status === 'completed') {
        this.result = job.result ?? null;
        this.error = null;
        this.loading = false;
      }
      if (job.status === 'failed') {
        this.error = job.error || 'El diagnóstico falló en el servidor.';
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.diagSub?.unsubscribe();
  }
}
